import { collection, doc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { quotaDayKey } from '@erectus/shared';
import type { CoachMessage } from '@erectus/shared';
import { firebase } from '@/lib/firebase.js';
import { getDocsServerFirst } from '@/lib/serverFirst.js';

/**
 * Darwin's conversations, one per local day: `users/{uid}/coachDays/{dayKey}`
 * holds the day's index entry and `messages` its turns. Written only by the
 * `askCoach` function (firestore.rules); read-only here.
 *
 * The app loads today's window, and earlier days only when the user opens the
 * archive — it never reads every message ever written. The day boundary is
 * the same local midnight the question allowance resets at.
 */
export interface CoachDay {
  dayKey: string;
  firstQuestion: string;
  messageCount: number;
}

/** Turns from before daily conversations, shown as one archive entry. */
export const LEGACY_DAY = 'earlier';

export function todayKey(now = Date.now()): string {
  return quotaDayKey(now, Intl.DateTimeFormat().resolvedOptions().timeZone);
}

function toMessages(docs: Array<{ id: string; data(): unknown }>): CoachMessage[] {
  return docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CoachMessage, 'id'>) }));
}

/** One day's conversation, oldest first. */
export async function loadCoachDay(uid: string, dayKey: string, max = 60): Promise<CoachMessage[]> {
  const db = firebase().db;
  const ref =
    dayKey === LEGACY_DAY
      ? collection(db, 'users', uid, 'coachThread')
      : collection(doc(db, 'users', uid, 'coachDays', dayKey), 'messages');
  const { snapshot } = await getDocsServerFirst(query(ref, orderBy('createdAt', 'desc'), limit(max)));
  return toMessages(snapshot.docs).reverse();
}

/** Today's window. */
export function loadCoachThread(uid: string): Promise<CoachMessage[]> {
  return loadCoachDay(uid, todayKey());
}

/** Earlier days, newest first — index entries only, not their messages. */
export async function listCoachDays(uid: string, max = 30): Promise<CoachDay[]> {
  const db = firebase().db;
  const today = todayKey();
  const [days, legacy] = await Promise.all([
    getDocs(query(collection(db, 'users', uid, 'coachDays'), orderBy('dayKey', 'desc'), limit(max + 1))),
    getDocs(query(collection(db, 'users', uid, 'coachThread'), orderBy('createdAt', 'asc'), limit(1))),
  ]);
  const list: CoachDay[] = days.docs
    .map((d) => d.data() as CoachDay)
    .filter((d) => d.dayKey !== today)
    .slice(0, max);
  const first = legacy.docs[0]?.data() as { text?: string } | undefined;
  if (first) list.push({ dayKey: LEGACY_DAY, firstQuestion: first.text ?? '', messageCount: 0 });
  return list;
}
